import axios, {
  AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from 'axios';
import { env } from '@/env';
import type { ApiSuccess, ApiError } from '@/types/api';

/**
 * Axios client + single-flight refresh pattern.
 *
 * Design constraints (PRD §11.1, §13.1, PLAN TASK 03):
 *   - Bearer access token attached from Zustand authStore (memory-only).
 *   - HttpOnly `refreshToken` cookie sent automatically (`withCredentials`).
 *     The cookie is scoped to /api/v1/auth on the backend so we explicitly
 *     hit `${API_BASE}/auth/refresh` from a SEPARATE axios instance that has
 *     NO interceptors — this prevents recursive refresh on 401 of the refresh
 *     endpoint itself.
 *   - On 401: queue concurrent failing requests behind a single in-flight
 *     refresh promise. When refresh resolves, drain the queue with the new
 *     token. When it rejects (refresh cookie expired or revoked), call
 *     onAuthFailure() which clears auth state and redirects to /login.
 *   - 4xx (except 401) is NOT retried — see queryClient.ts retry policy.
 */

interface RetriableConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

// ── Auth bridge ───────────────────────────────────────────────────────────────
// The api/client.ts module cannot import authStore directly without creating
// a circular dependency (authStore consumes apiClient.post('/auth/login')).
// Instead, expose register hooks that authStore will call once at boot.

type TokenGetter = () => string | null;
type TokenSetter = (token: string) => void;
type AuthFailureHandler = () => void;

let getAccessToken: TokenGetter = () => null;
let setAccessToken: TokenSetter = () => {};
let onAuthFailure: AuthFailureHandler = () => {};

export function configureAuthBridge(opts: {
  getAccessToken: TokenGetter;
  setAccessToken: TokenSetter;
  onAuthFailure: AuthFailureHandler;
}): void {
  getAccessToken = opts.getAccessToken;
  setAccessToken = opts.setAccessToken;
  onAuthFailure = opts.onAuthFailure;
}

// ── Clients ───────────────────────────────────────────────────────────────────

const baseConfig: AxiosRequestConfig = {
  baseURL: env.VITE_API_BASE_URL,
  withCredentials: true,
  timeout: 15_000,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
};

/** Main client — used everywhere except the refresh endpoint. */
export const apiClient: AxiosInstance = axios.create(baseConfig);

/** Refresh client — NO interceptors. Used only by the refresh interceptor below. */
const refreshClient: AxiosInstance = axios.create(baseConfig);

// ── Request interceptor: attach access token ──────────────────────────────────

apiClient.interceptors.request.use((config) => {
  const token = getAccessToken();
  if (token) {
    config.headers.set('Authorization', `Bearer ${token}`);
  }
  return config;
});

// ── Response interceptor: single-flight refresh on 401 ────────────────────────

let refreshInFlight: Promise<string> | null = null;

async function performRefresh(): Promise<string> {
  if (refreshInFlight) return refreshInFlight;

  refreshInFlight = (async () => {
    try {
      const res = await refreshClient.post<ApiSuccess<{ accessToken: string }>>(
        '/auth/refresh'
      );
      const newToken = res.data?.data?.accessToken;
      if (!newToken) {
        throw new Error('Refresh response missing accessToken');
      }
      setAccessToken(newToken);
      return newToken;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<ApiError>) => {
    const original = error.config as RetriableConfig | undefined;
    const status = error.response?.status;

    // No retry: no original config, not 401, already retried, or it IS the refresh endpoint
    if (
      !original ||
      status !== 401 ||
      original._retry ||
      original.url?.includes('/auth/refresh') ||
      original.url?.includes('/auth/login') ||
      original.url?.includes('/auth/register')
    ) {
      return Promise.reject(error);
    }

    original._retry = true;

    try {
      const newToken = await performRefresh();
      // Update the auth header on the queued request and replay it.
      original.headers.set('Authorization', `Bearer ${newToken}`);
      return apiClient.request(original);
    } catch (refreshErr) {
      // Refresh failed — refresh cookie expired/revoked. Hard-logout.
      onAuthFailure();
      return Promise.reject(refreshErr);
    }
  }
);

// ── Helpers ───────────────────────────────────────────────────────────────────

/** Unwrap an envelope so callers receive `T` instead of `ApiSuccess<T>`. */
export function unwrap<T>(response: { data: ApiSuccess<T> }): T {
  return response.data.data;
}

/** Get an `ApiSuccess<T>` directly when pagination/message are needed. */
export function unwrapEnvelope<T>(response: {
  data: ApiSuccess<T>;
}): ApiSuccess<T> {
  return response.data;
}

/** Type predicate for axios errors with our backend error envelope. */
export function isApiError(err: unknown): err is AxiosError<ApiError> {
  return axios.isAxiosError(err) && Boolean(err.response?.data?.error);
}

/** Extract a user-friendly message from any thrown API error. */
export function getApiErrorMessage(err: unknown, fallback = 'Something went wrong'): string {
  if (isApiError(err)) {
    return err.response?.data?.error?.message ?? fallback;
  }
  if (err instanceof Error) return err.message;
  return fallback;
}

/** Extract the backend error code (e.g. 'EMAIL_NOT_VERIFIED') from a thrown API error. */
export function getApiErrorCode(err: unknown): string | undefined {
  return isApiError(err) ? err.response?.data?.error?.code : undefined;
}
